/**
 * useStaleSyncGate — Decision 7 in BIGXBOOK_WEB_PLAN.md.
 *
 * A browser that last synced weeks ago still holds a full catalogue. Billing
 * from it means selling at prices that may have changed and against stock that
 * may be long gone, and the resulting invoices are painful to correct after the
 * fact. Desktop carries the same exposure but is used daily; "any browser, any
 * device" makes a months-old cache genuinely likely.
 *
 * So: offline billing is allowed only when this device synced recently.
 *
 * Deliberately NOT a blanket block:
 *   • Online and reachable → always allowed. The gate exists to stop billing on
 *     a stale *offline* cache, not to stop billing.
 *   • Never synced → blocked. A device with no catalogue cannot bill sensibly.
 *   • Synced within the window → allowed, with the age surfaced once it is more
 *     than half spent, so the operator sees it coming rather than hitting a
 *     wall mid-shift.
 */
import { useEffect, useState } from 'react';
import { useAppStore } from '@/store/useAppStore';
import { netInfo } from '@/platform';

/** Default staleness window. Plan open item 2 — confirm before production. */
export const STALE_SYNC_DAYS = 7;

const DAY_MS = 24 * 60 * 60 * 1000;

export interface StaleSyncState {
  /** True while reachability is still being determined. */
  checking: boolean;
  /** True when billing must be blocked until a successful sync. */
  blocked: boolean;
  /** True when billing is allowed but the cache is getting old. */
  warn: boolean;
  /** Whole days since the last successful sync; null when never synced. */
  ageDays: number | null;
  /** Whether the backend answered a reachability probe. */
  online: boolean;
  reason: 'never-synced' | 'stale' | 'ok';
}

export function useStaleSyncGate(windowDays = STALE_SYNC_DAYS): StaleSyncState {
  const lastSyncAt = useAppStore((s) => s.lastSyncAt);
  const [online, setOnline] = useState<boolean | null>(null);

  useEffect(() => {
    let alive = true;
    const probe = () => {
      void netInfo.isOnline().then((v) => alive && setOnline(v));
    };
    probe();
    const unsub = netInfo.subscribe(probe);
    return () => {
      alive = false;
      unsub();
    };
  }, []);

  // Re-evaluate after every sync, so clearing the block does not need a reload.
  const ageMs = lastSyncAt ? Date.now() - lastSyncAt : null;
  const ageDays = ageMs === null ? null : Math.floor(ageMs / DAY_MS);
  const isStale = ageMs === null || ageMs > windowDays * DAY_MS;

  if (online === null) {
    return { checking: true, blocked: false, warn: false, ageDays, online: false, reason: 'ok' };
  }

  // Reachable: sync can fix staleness on demand, so never block.
  if (online) {
    return {
      checking: false,
      blocked: false,
      warn: isStale,
      ageDays,
      online: true,
      reason: isStale ? (lastSyncAt ? 'stale' : 'never-synced') : 'ok',
    };
  }

  if (!lastSyncAt) {
    return { checking: false, blocked: true, warn: false, ageDays: null, online: false, reason: 'never-synced' };
  }

  return {
    checking: false,
    blocked: isStale,
    // Warn once more than half the window is spent.
    warn: !isStale && ageMs !== null && ageMs > (windowDays * DAY_MS) / 2,
    ageDays,
    online: false,
    reason: isStale ? 'stale' : 'ok',
  };
}
